import { Fragment } from "react";
import type { ReactElement, ReactNode } from "react";

import capitalize from "lodash/capitalize";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import type { INavItemBadgeType } from "@similarweb/ui-components";
import { EPrimaryItemType, PixelPlaceholderLoader, SubsectionItem } from "@similarweb/ui-components";

import type { ISwSettings } from "app/@types/ISwSettings";
import { sidebarTrackingDispatcher } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { translate } from "app/filters/i18nFilter";
import { getShowGetStarted } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import { toggleBranchName } from "app/services/dev-toggle/devToggle";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import {
    isNoTouchOrTrialOrAfterTrial,
    isPendingTrialStatus,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { useTrack } from "pro-features/components/WithTrack/src/useTrack";

const ADDONS_CLICK_EVENT_NAME = "settings and help/add ons";

interface IComponentAttributes {
    type?: EPrimaryItemType;
    key?: string;
    icon?: IconName;
    isSelected?: boolean;
    itemText?: string;
    pillText?: string;
    navBadgeType?: INavItemBadgeType;
    shouldBeDisplayed?: boolean;
    onItemClick?: VoidFunction;
    hrefLink?: string;
    isLoading?: boolean;
    eventName?: string;
    isBottomComponent?: boolean;
}

declare const SW_ENV: { debug: boolean; version: string };
const ItemsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const DividerWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: 12px 0;
`;
const Divider = styled.div`
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};
    width: 196px;
`;

interface IGetDropDownContent {
    canPurchaseNoTouch: string;
    subscriptionDetailsUrl: string;
    accountDetailsUrl: string;
    workspace: string;
    impersonateMode: boolean;
    canImpersonate: boolean;
    hasLicense: boolean;
    toggleDevTools: VoidFunction;
    claimsOverwritten: boolean;
    productsOverwritten: boolean;
    canViewSubscriptionClaimsPage: boolean;
    swSettings: ISwSettings;
    userManagementLinksContainer: ReactNode[];
    resetClaims: string;
    supportUrl: string;
    supportHomepageUrl: string;
    logoutClick: VoidFunction;
    inviteTeammate: VoidFunction;
    openRewards: VoidFunction;
    backTo: VoidFunction;
    impersonateSourceEmail: string;
    canViewSubscriptionProductsPage: boolean;
    version: string;
    branch: string;
    resetUiVersion: VoidFunction;
    resetUiBranch: VoidFunction;
    openImpersonatePopUp: VoidFunction;
    resetAppsAddon: VoidFunction;
    onGetStarted: VoidFunction;
    isDMITrialAfterTrial: boolean;
    onResetDefaultHomepage: VoidFunction;
    shouldDisplayResetHomepage: boolean;
    onOpenContactSalesModal: VoidFunction;
}

const DividerComponent = () => (
    <DividerWrapper>
        <Divider />
    </DividerWrapper>
);

export const getDropDownContent = ({
    canPurchaseNoTouch,
    subscriptionDetailsUrl,
    accountDetailsUrl,
    workspace,
    impersonateMode,
    canImpersonate,
    toggleDevTools,
    claimsOverwritten,
    productsOverwritten,
    canViewSubscriptionClaimsPage,
    swSettings,
    userManagementLinksContainer,
    resetClaims,
    supportUrl,
    supportHomepageUrl,
    logoutClick,
    inviteTeammate,
    openRewards,
    backTo,
    impersonateSourceEmail,
    canViewSubscriptionProductsPage,
    version,
    branch,
    resetUiVersion,
    resetUiBranch,
    openImpersonatePopUp,
    resetAppsAddon,
    onGetStarted,
    isDMITrialAfterTrial,
    onResetDefaultHomepage,
    shouldDisplayResetHomepage,
    onOpenContactSalesModal,
}: IGetDropDownContent): ReactNode[] => {
    const [track] = useTrack();
    const urlPrefix = canPurchaseNoTouch ? subscriptionDetailsUrl : accountDetailsUrl;
    const accountLink = `${urlPrefix}?returnToProUrl=${encodeURIComponent(location.href)}`;
    const type = EPrimaryItemType.StandalonePrimary;

    const { hasAddonsHub } = useAddonsState();
    const isNTorTrialOrAfterTrialOrCC = isNoTouchOrTrialOrAfterTrial(swSettings) || isPendingTrialStatus(swSettings);

    const settingsComponentsTracking = (name, idx, eventName, badgeType = "none") => {
        const trackingItemProps = {
            depth: 2,
            type: "click",
            report: null,
            report_group: null,
            report_domain: name,
            analytics_domain: "Settings",
            position_order: idx + 1,
            is_dropdown_list: false,
            is_locked: false,
            tag: badgeType,
            eventName,
        };
        sidebarTrackingDispatcher(trackingItemProps);
    };

    const components: IComponentAttributes[] = [
        {
            type,
            key: "devToggle",
            onItemClick: toggleDevTools,
            itemText: "toggle dev tools",
            pillText: "dev only",
            navBadgeType: BADGE_ITEMS.INTERNAL,
            shouldBeDisplayed: SW_ENV.debug,
            icon: "sidebar3-dev-tools",
        },
        {
            type,
            key: "branchNameToggle",
            onItemClick: toggleBranchName,
            itemText: "toggle Branch name",
            pillText: "dev only",
            navBadgeType: BADGE_ITEMS.INTERNAL,
            shouldBeDisplayed: SW_ENV.debug,
            icon: "sidebar3-dev-tools",
        },
        {
            type,
            icon: "fire-outline",
            itemText: translate("sidebar.userSettings.getStarted"),
            key: "getStarted",
            onItemClick: onGetStarted,
            shouldBeDisplayed: getShowGetStarted(),
        },
        {
            type,
            hrefLink: accountLink,
            icon: "sidebar3-account",
            itemText: translate("Usermanagement.Account.Title"),
            key: "account",
            pillText: canPurchaseNoTouch ? `Usermanagement.Account.NoTouchPackage.${capitalize(workspace)}` : "",
            shouldBeDisplayed: true,
            eventName: "settings and help/account",
        },
        {
            type,
            icon: "sidebar3-updates-news",
            itemText: translate("sidebar.explore.plans.title"),
            key: "explorePlans",
            shouldBeDisplayed: isNTorTrialOrAfterTrialOrCC,
            eventName: "settings and help/explore plans",
            onItemClick: () => {
                track("Sidebar interaction", "click", "settings and help/explore plans");
                goToPackagesPage();
            },
        },
        {
            type,
            icon: "gift",
            itemText: translate("sidebar.rewards.title"),
            key: "rewards",
            onItemClick: openRewards,
            shouldBeDisplayed: isDMITrialAfterTrial,
        },
        {
            type,
            icon: "snippet-answer",
            itemText: translate("sidebar.contactSales.title"),
            key: "contactSales",
            onItemClick: onOpenContactSalesModal,
            shouldBeDisplayed: isNTorTrialOrAfterTrialOrCC,
            eventName: "settings and help/contact sales",
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
        },
        {
            type,
            icon: "sidebar3-support",
            itemText: translate("support.iconbar.title"),
            key: "support",
            onItemClick: () => window.open(supportHomepageUrl),
            shouldBeDisplayed: true,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
            eventName: "settings and help/support",
        },
        {
            type,
            icon: "brick",
            itemText: translate("sidebar.goToAddonHub.title"),
            key: "goToAddonHub",
            onItemClick: () => {
                track("Sidebar interaction", "click", ADDONS_CLICK_EVENT_NAME);
                goToAddonsPage({});
            },
            shouldBeDisplayed: hasAddonsHub,
            eventName: ADDONS_CLICK_EVENT_NAME,
        },
        {
            type,
            icon: "users",
            itemText: translate("sidebar.inviteTeammate.title"),
            key: "inviteTeammate",
            onItemClick: inviteTeammate,
            shouldBeDisplayed: true,
            eventName: "settings and help/invite a teammate",
        },
        {
            type,
            hrefLink: supportUrl,
            key: "internalSupport",
            itemText: translate("Usermanagement.ContactSupport.Title"),
            pillText: translate("Usermanagement.Internal.Title"),
            navBadgeType: BADGE_ITEMS.INTERNAL,
            icon: "sidebar3-support-internal",
            shouldBeDisplayed: canImpersonate,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
            eventName: "settings and help/internal support",
        },
        {
            type: EPrimaryItemType.Impersonate,
            key: "impersonate",
            icon: "sidebar3-impersonate",
            isSelected: impersonateMode,
            itemText: translate("Usermanagement.Impersonate.Title"),
            pillText: translate("Usermanagement.Internal.Title"),
            navBadgeType: BADGE_ITEMS.INTERNAL,
            shouldBeDisplayed: canImpersonate,
            onItemClick: openImpersonatePopUp,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
        },
        {
            type,
            itemText: `Back to ${impersonateSourceEmail.slice(0, 15)}`,
            icon: "sidebar3-impersonate-turn-off",
            onItemClick: backTo,
            key: "backToOriginalUser",
            shouldBeDisplayed: impersonateMode,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
        },
        {
            type,
            key: "packageDemoTool",
            hrefLink: `${swSettings.swsites.login}/package-demo-tool`,
            isSelected: productsOverwritten && canViewSubscriptionProductsPage,
            icon: "sidebar3-package-manipulation",
            itemText: "Package demo tool",
            pillText: translate("Usermanagement.Internal.Title"),
            navBadgeType: BADGE_ITEMS.INTERNAL,
            shouldBeDisplayed: canViewSubscriptionProductsPage && !impersonateMode,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
        },
        {
            type,
            key: "resetDefaultPage",
            icon: "homepage",
            onItemClick: onResetDefaultHomepage,
            itemText: translate("Usermanagement.ResetHomepage.Title"),
            pillText: translate("Usermanagement.Internal.Title"),
            navBadgeType: BADGE_ITEMS.INTERNAL,
            shouldBeDisplayed: shouldDisplayResetHomepage,
            isBottomComponent: isNTorTrialOrAfterTrialOrCC,
        },
        {
            type,
            hrefLink: `${swSettings.swsites.login}/subscription-manipulation`,
            isSelected: claimsOverwritten && canViewSubscriptionClaimsPage,
            key: "subscriptionManipulation",
            icon: "sidebar3-package-manipulation",
            itemText: translate("usersettingsdropdown.subscription.manipulation"),
            shouldBeDisplayed: canViewSubscriptionClaimsPage,
        },
        {
            type,
            hrefLink: resetClaims,
            onItemClick: resetAppsAddon,
            key: "resetClaims",
            icon: "sidebar3-package-manipulation-turn-off",
            itemText: translate("usersettingsdropdown.reset.claims"),
            shouldBeDisplayed: claimsOverwritten || productsOverwritten,
        },
        {
            type,
            itemText: `Reset Ui Version`,
            icon: "sidebar3-ui-version-turn-off",
            onItemClick: resetUiVersion,
            key: "resetUiVersion",
            shouldBeDisplayed: Boolean(version),
        },
        {
            type,
            itemText: `Reset UI Branch`,
            icon: "sidebar3-ui-version-turn-off",
            onItemClick: resetUiBranch,
            key: "resetUiBranch",
            shouldBeDisplayed: Boolean(branch),
        },
        {
            type,
            icon: "sidebar3-logout",
            itemText: translate("shared.branding.signout"),
            key: "logout",
            onItemClick: logoutClick,
            shouldBeDisplayed: true,
        },
    ];
    let logoutComponent: ReactElement<{ ISubsectionItem }>;
    let bottomComponentsList: ReactElement<{ ISubsectionItem }>[] = [];
    const itemsComponentsList: ReactElement<{ ISubsectionItem }>[] = [];
    let itemCounter = 0;

    components.forEach((component, idx) => {
        const {
            type,
            key,
            icon,
            isSelected,
            itemText,
            pillText,
            shouldBeDisplayed,
            onItemClick,
            hrefLink,
            navBadgeType,
            isLoading,
            eventName,
            isBottomComponent,
        } = component;
        if (key === "logout") {
            itemCounter++;
            logoutComponent = (
                <SubsectionItem
                    key={key}
                    itemText={itemText}
                    icon={icon}
                    onItemClick={() => {
                        settingsComponentsTracking(key, itemCounter, navBadgeType);
                        onItemClick?.();
                    }}
                    itemType={type}
                />
            );
        } else if (shouldBeDisplayed) {
            itemCounter++;
            const itemComponent = (
                <SubsectionItem
                    key={key}
                    itemType={type}
                    itemText={itemText}
                    icon={icon}
                    onItemClick={() => {
                        settingsComponentsTracking(key, itemCounter, eventName, navBadgeType);
                        onItemClick?.();
                    }}
                    hrefLink={hrefLink}
                    badgeText={pillText}
                    navBadgeType={navBadgeType}
                    isSelected={isSelected}
                />
            );
            const LoaderComponent = (
                <PixelPlaceholderLoader
                    width={"100%"}
                    height={30}
                />
            );
            if (isBottomComponent) {
                bottomComponentsList.push(isLoading ? LoaderComponent : itemComponent);
            } else {
                itemsComponentsList.push(isLoading ? LoaderComponent : itemComponent);
            }
        }
    });
    let itemsComponent: ReactNode = (
        <ItemsWrapper key="userSettingsTopItemsWrapper">{itemsComponentsList}</ItemsWrapper>
    );
    const bottomComponent: ReactNode = (
        <Fragment key="bottomSection">
            <ItemsWrapper key="userSettingsBottomItemsWrapper">{bottomComponentsList}</ItemsWrapper>
            <DividerComponent key="bottomDivider" />
        </Fragment>
    );
    if (userManagementLinksContainer?.length > 0) {
        itemsComponent = userManagementLinksContainer.concat(itemsComponent);
    }
    return [
        itemsComponent,
        <DividerComponent key="topDivider" />,
        isNTorTrialOrAfterTrialOrCC ? bottomComponent : <Fragment key="bottomSection" />,
        logoutComponent,
    ];
};
